import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  Link2,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import { supabase } from '../../services/adminService';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '../../config/env';

const FN = `${SUPABASE_URL.replace(/\/+$/, '')}/functions/v1/gbp-posts`;

const CTA = [
  { id: 'LEARN_MORE', label: 'Learn more' },
  { id: 'BOOK', label: 'Book' },
  { id: 'CALL', label: 'Call' },
  { id: 'SIGN_UP', label: 'Sign up' },
  { id: 'NONE', label: 'No button' },
];

const TEMPLATES = [
  {
    label: 'Jump start 24/7',
    cta_type: 'BOOK',
    cta_url: 'https://app.roadserve.in/rsa',
    summary:
      'Dead battery? RoadServe jump-starts at your location 24/7. Book in the app, share live pin, and track the partner. Quote before dispatch.',
  },
  {
    label: 'Car towing',
    cta_type: 'BOOK',
    cta_url: 'https://app.roadserve.in/rsa',
    summary:
      'Need a towing van? RoadServe dispatches 24/7 towing at your pin. Book on the website or app. Quote before the van moves.',
  },
  {
    label: 'Flat tyre',
    cta_type: 'LEARN_MORE',
    cta_url: 'https://app.roadserve.in/services/jump-start',
    summary:
      'Puncture on the road? RoadServe doorstep flat tyre help. Spare change or repair at your location — nights and holidays included.',
  },
  {
    label: 'Download app',
    cta_type: 'LEARN_MORE',
    cta_url: 'https://app.roadserve.in/go/roadserveapp',
    summary:
      'Fastest RSA booking is the RoadServe app. Share live GPS, track the partner, jump start, towing and puncture — 24/7.',
  },
  {
    label: 'RSA membership',
    cta_type: 'SIGN_UP',
    cta_url: 'https://app.roadserve.in/membership',
    summary:
      'RoadServe RSA membership from Rs 99/month. Jump start, towing, flat tyre and more — or pay only when you need help.',
  },
];

function istLocalValue(d = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

function fromIstInput(value) {
  if (!value) return new Date().toISOString();
  return new Date(`${value}:00+05:30`).toISOString();
}

async function callGbp(body, search = '') {
  const res = await fetch(`${FN}${search}`, {
    method: body ? 'POST' : 'GET',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

const emptyForm = () => ({
  summary: TEMPLATES[0].summary,
  cta_type: 'BOOK',
  cta_url: 'https://app.roadserve.in/rsa',
  media_url: '',
  scheduled_at: istLocalValue(new Date(Date.now() + 60 * 60 * 1000)),
  recurrence: 'weekly',
});

const statusClass = (s) => {
  if (s === 'published') return 'bg-emerald-50 text-emerald-700';
  if (s === 'scheduled') return 'bg-sky-50 text-sky-700';
  if (s === 'failed') return 'bg-red-50 text-red-700';
  return 'bg-gray-100 text-gray-600';
};

export default function GoogleBusinessPosts() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [banner, setBanner] = useState('');
  const [locs, setLocs] = useState([]);

  useEffect(() => {
    const gbp = params.get('gbp');
    if (!gbp) return;
    if (gbp === 'connected') setBanner('Google account connect ho gaya. Ab listing choose karo.');
    if (gbp === 'error') setBanner(params.get('msg') || 'Google connect fail');
    params.delete('gbp');
    params.delete('msg');
    setParams(params, { replace: true });
  }, [params, setParams]);

  const statusQ = useQuery({
    queryKey: ['gbpStatus'],
    queryFn: () => callGbp(null, '?action=status'),
    retry: false,
  });

  const postsQ = useQuery({
    queryKey: ['gbpPosts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gbp_posts')
        .select('*')
        .order('scheduled_at', { ascending: false })
        .limit(80);
      if (error) throw error;
      return data || [];
    },
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('gbp_posts').insert({
        summary: form.summary.trim(),
        topic_type: 'STANDARD',
        cta_type: form.cta_type === 'NONE' ? null : form.cta_type,
        cta_url: form.cta_url.trim() || null,
        media_url: form.media_url.trim() || null,
        scheduled_at: fromIstInput(form.scheduled_at),
        recurrence: form.recurrence,
        status: 'scheduled',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gbpPosts'] });
      setForm(emptyForm());
      setBanner('Post queue mein aa gayi. Time pe Google pe publish hogi.');
    },
  });

  const delMut = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('gbp_posts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gbpPosts'] }),
  });

  const pubMut = useMutation({
    mutationFn: (id) => callGbp({ action: 'publish-now', id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gbpPosts'] });
      setBanner('Google pe post live ho gayi.');
    },
  });

  const connectMut = useMutation({
    mutationFn: () =>
      callGbp({
        action: 'oauth-start',
        returnTo: `${window.location.origin}/google-business-posts`,
      }),
    onSuccess: (data) => {
      if (data.url) window.location.href = data.url;
    },
  });

  const locMut = useMutation({
    mutationFn: () => callGbp({ action: 'locations' }),
    onSuccess: (data) => setLocs(data.locations || []),
  });

  const pickMut = useMutation({
    mutationFn: (loc) =>
      callGbp({
        action: 'save-location',
        google_account_name: loc.account,
        google_account_label: loc.accountLabel,
        location_name: loc.name,
        location_title: loc.title,
        v4_parent: loc.v4Parent,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gbpStatus'] });
      setBanner('Listing save ho gayi — RoadServe Maps profile.');
    },
  });

  const weekMut = useMutation({
    mutationFn: async () => {
      const start = new Date();
      start.setHours(start.getHours() + 1);
      const rows = TEMPLATES.map((t, i) => {
        const when = new Date(start);
        when.setDate(when.getDate() + i);
        when.setHours(10, 0, 0, 0);
        return {
          summary: t.summary,
          topic_type: 'STANDARD',
          cta_type: t.cta_type,
          cta_url: t.cta_url,
          scheduled_at: when.toISOString(),
          recurrence: 'weekly',
          status: 'scheduled',
        };
      });
      const { error } = await supabase.from('gbp_posts').insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gbpPosts'] });
      setBanner('5 posts schedule ho gayi (weekly repeat, 10:00 IST).');
    },
  });

  const conn = statusQ.data?.connection;
  const connected = Boolean(statusQ.data?.connected);
  const hasClient = Boolean(statusQ.data?.hasClient);
  const err = saveMut.error || connectMut.error || pubMut.error || locMut.error || pickMut.error || weekMut.error || postsQ.error;

  const upcoming = useMemo(
    () => (postsQ.data || []).filter((p) => p.status === 'scheduled'),
    [postsQ.data],
  );

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/more')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft size={16} /> More Options
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google Business Posts</h1>
          <p className="text-sm text-gray-500 mt-1">
            Maps listing <span className="font-medium">RoadServe</span> pe auto-schedule. Strength ke liye phone / photos alag Google pe bharo — yeh sirf posts hai.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            statusQ.refetch();
            postsQ.refetch();
          }}
          className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {banner ? (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{banner}</div>
      ) : null}
      {err ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err.message}</div>
      ) : null}

      <section className="mb-6 rounded-xl border bg-white p-4">
        <div className="flex items-center gap-2 font-semibold text-gray-900">
          <MapPin size={18} className="text-[#1B4F9C]" /> Profile connect
        </div>
        <p className="mt-1 text-sm text-gray-500">
          {connected
            ? conn?.location_title
              ? `Connected: ${conn.location_title}`
              : 'Google login ho gaya — listing choose karo.'
            : 'Pehle wahi Google account se connect karo jisse Maps listing manage hoti hai.'}
        </p>
        {!hasClient ? (
          <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Supabase secret set karo: <code>GBP_GOOGLE_CLIENT_ID</code>, <code>GBP_GOOGLE_CLIENT_SECRET</code>.
            Google Cloud → OAuth Web client. Redirect URI:{' '}
            <code className="break-all">{statusQ.data?.redirectUri || `${FN}?action=oauth-callback`}</code>
            . Enable: My Business Account Management, Business Information, Google My Business API.
          </p>
        ) : null}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={connectMut.isPending}
            onClick={() => connectMut.mutate()}
            className="inline-flex items-center gap-2 rounded-lg bg-[#1B4F9C] px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {connectMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
            Connect Google
          </button>
          {connected ? (
            <button
              type="button"
              disabled={locMut.isPending}
              onClick={() => locMut.mutate()}
              className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
            >
              {locMut.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
              Load listings
            </button>
          ) : null}
        </div>
        {locs.length > 0 ? (
          <ul className="mt-3 divide-y rounded-lg border">
            {locs.map((loc) => (
              <li key={loc.v4Parent} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span>
                  <span className="font-medium">{loc.title}</span>
                  <span className="block text-xs text-gray-500">{loc.accountLabel}</span>
                </span>
                <button
                  type="button"
                  className="text-[#1B4F9C] font-medium"
                  onClick={() => pickMut.mutate(loc)}
                >
                  Use this
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="mb-6 rounded-xl border bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-semibold">
            <Plus size={18} className="text-[#1B4F9C]" /> Schedule post
          </div>
          <button
            type="button"
            disabled={weekMut.isPending}
            onClick={() => weekMut.mutate()}
            className="text-sm text-[#1B4F9C] font-medium"
          >
            {weekMut.isPending ? 'Adding…' : 'Add 5 weekly RSA posts'}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {TEMPLATES.map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={() => setForm((f) => ({ ...f, summary: t.summary, cta_type: t.cta_type, cta_url: t.cta_url }))}
              className="rounded-full border px-3 py-1 text-xs hover:bg-gray-50"
            >
              {t.label}
            </button>
          ))}
        </div>

        <textarea
          className="mt-3 w-full rounded-lg border px-3 py-2 text-sm min-h-[110px]"
          maxLength={1500}
          value={form.summary}
          onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
        />
        <p className="text-xs text-gray-400 text-right">{form.summary.length}/1500</p>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            Button
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2"
              value={form.cta_type}
              onChange={(e) => setForm((f) => ({ ...f, cta_type: e.target.value }))}
            >
              {CTA.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            Button URL
            <input
              className="mt-1 w-full rounded-lg border px-3 py-2"
              value={form.cta_url}
              onChange={(e) => setForm((f) => ({ ...f, cta_url: e.target.value }))}
              placeholder="https://app.roadserve.in/rsa"
            />
          </label>
          <label className="text-sm">
            Publish (IST)
            <input
              type="datetime-local"
              className="mt-1 w-full rounded-lg border px-3 py-2"
              value={form.scheduled_at}
              onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
            />
          </label>
          <label className="text-sm">
            Repeat
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2"
              value={form.recurrence}
              onChange={(e) => setForm((f) => ({ ...f, recurrence: e.target.value }))}
            >
              <option value="none">Once</option>
              <option value="weekly">Every week</option>
            </select>
          </label>
          <label className="text-sm sm:col-span-2">
            Photo URL (optional, public https)
            <input
              className="mt-1 w-full rounded-lg border px-3 py-2"
              value={form.media_url}
              onChange={(e) => setForm((f) => ({ ...f, media_url: e.target.value }))}
            />
          </label>
        </div>

        <button
          type="button"
          disabled={saveMut.isPending || !form.summary.trim()}
          onClick={() => saveMut.mutate()}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#1B4F9C] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          {saveMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
          Save schedule
        </button>
      </section>

      <section className="rounded-xl border bg-white overflow-hidden">
        <div className="px-4 py-3 border-b font-semibold text-sm">
          Queue {upcoming.length ? `· ${upcoming.length} upcoming` : ''}
        </div>
        {postsQ.isLoading ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : (postsQ.data || []).length === 0 ? (
          <div className="p-6 text-sm text-gray-500">Abhi koi post nahi. Template choose karke Save schedule dabao.</div>
        ) : (
          <ul className="divide-y">
            {(postsQ.data || []).map((p) => (
              <li key={p.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(p.status)}`}>{p.status}</span>
                    {p.recurrence === 'weekly' ? (
                      <span className="text-xs text-gray-500">weekly</span>
                    ) : null}
                    <span className="text-xs text-gray-500">
                      {new Date(p.scheduled_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-800 line-clamp-3">{p.summary}</p>
                  {p.last_error ? <p className="mt-1 text-xs text-red-600">{p.last_error}</p> : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.status !== 'published' ? (
                    <button
                      type="button"
                      title="Publish now"
                      onClick={() => pubMut.mutate(p.id)}
                      className="rounded-lg border p-2 text-gray-600 hover:text-[#1B4F9C]"
                    >
                      <Send size={14} />
                    </button>
                  ) : (
                    <CheckCircle2 size={16} className="text-emerald-600" />
                  )}
                  <button
                    type="button"
                    title="Delete"
                    onClick={() => delMut.mutate(p.id)}
                    className="rounded-lg border p-2 text-gray-600 hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="mt-4 text-xs text-gray-400 flex items-center gap-1">
        <ExternalLink size={12} />
        Profile strength Google Search listing pe complete karo (phone, photos, reviews). Yeh page posts publish karti hai.
      </p>
      {statusQ.isError ? (
        <p className="mt-2 text-xs text-red-600 inline-flex items-center gap-1">
          <XCircle size={12} /> Function deploy nahi / table missing. SQL <code>supabase/gbp-posts.sql</code> run karo.
        </p>
      ) : null}
    </div>
  );
}
